import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "system",
  setTheme: () => {},
  isDark: false,
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>("system");
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme") as Theme | null;
    const initialTheme = storedTheme || "system";
    setTheme(initialTheme);
    updateTheme(initialTheme);
  }, []);

  useEffect(() => {
    updateTheme(theme);
    if (theme !== "system") {
      localStorage.setItem("theme", theme);
    }
  }, [theme]);

  const updateTheme = (theme: Theme) => {
    const isDarkMode =
      theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : theme === "dark";

    setIsDark(isDarkMode);
    document.documentElement.classList.toggle("dark", isDarkMode);
    document.documentElement.classList.toggle("light", theme === "light");

    if (theme === "light") {
      document.body.setAttribute("data-weui-theme", "light");
    } else if (theme === "dark") {
      document.body.setAttribute("data-weui-theme", "dark");
    } else {
      document.body.removeAttribute("data-weui-theme");
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
